/**
 * PageBanner Component
 * Handles dynamic background color matching with the next section
 * Optimized for performance on critical page elements
 */

interface PageBannerProps {
    el: HTMLElement;
}

const PageBanner = ({ el }: PageBannerProps): void => {
    const getNextSection = (element: HTMLElement): HTMLElement | null => {
        const currentSection = element.closest('section');
        if (!currentSection) {
            return null;
        }

        // Find the next section sibling
        let nextElement = currentSection.nextElementSibling;
        while (nextElement) {
            if (nextElement.tagName.toLowerCase() === 'section') {
                return nextElement as HTMLElement;
            }
            nextElement = nextElement.nextElementSibling;
        }
        return null;
    };

    const setDynamicBackgroundColor = (): void => {
        const nextSection = getNextSection(el);
        if (!nextSection) {
            return;
        }

        const bgColor = window.getComputedStyle(nextSection).backgroundColor;

        // Only apply if we get a solid color (not transparent/default)
        if (bgColor &&
            bgColor !== 'rgba(0, 0, 0, 0)' &&
            bgColor !== 'transparent' &&
            bgColor !== 'initial' &&
            bgColor !== 'inherit') {
            el.style.setProperty('--dynamic-bg-color', bgColor);
            el.classList.add('has-dynamic-bg');
        }
    };

    // Use requestAnimationFrame to defer execution until after critical rendering
    requestAnimationFrame(() => {
        if (document.contains(el)) {
            setDynamicBackgroundColor();
        }
    });
};

export default PageBanner;
